import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { PaginatedResponse } from '../models/paginatedResponse';
import { MessageDto, MessageAnswerDto, MessageRecipientUserInfo } from '../models/api/messageDto';
import { MessageCreateRequest } from '../models/api/messageCreateRequest';
import { MessageReplyRequest } from '../models/api/messageReplyRequest';
import { MessageReadStatusRequest } from '../models/api/messageReadStatusRequest';
import { UnreadCountResponse } from '../models/api/unreadCountResponse';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MessageService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/messages`;

  /** Shared unread count signal — any component can read this reactively */
  unreadCount = signal(0);

  /** Fetch unread count from API and update the shared signal */
  refreshUnreadCount(): void {
    this.getUnreadCount().subscribe({
      next: (response) => this.unreadCount.set(response.unreadCount),
      error: () => this.unreadCount.set(0)
    });
  }

  // --- Unread-count polling ---------------------------------------------------
  // Keeps the notifications badge live without a manual page refresh. The server
  // is the source of truth: each tick re-reads getUnreadCount(), so once a
  // message is marked read server-side (opening it, or "mark all as read") the
  // next poll reflects the lower count and never resurrects it as unread.
  // Mirrors the trigger pattern in UpdateCheckService (interval + visibility + focus).

  private readonly pollIntervalMs = 60_000;
  private pollHandle?: ReturnType<typeof setInterval>;
  private pollStarted = false;
  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      this.refreshUnreadCount();
    }
  };
  private readonly onFocus = (): void => this.refreshUnreadCount();

  /**
   * Begin polling the unread count so the notifications badge updates on its own.
   * Idempotent — safe to call once when the messaging UI mounts.
   */
  startPolling(): void {
    if (this.pollStarted) {
      return;
    }
    this.pollStarted = true;
    this.refreshUnreadCount();
    this.pollHandle = setInterval(() => this.refreshUnreadCount(), this.pollIntervalMs);
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('focus', this.onFocus);
  }

  /** Stop polling (e.g. on logout or when the messaging UI unmounts). */
  stopPolling(): void {
    if (!this.pollStarted) {
      return;
    }
    this.pollStarted = false;
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = undefined;
    }
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('focus', this.onFocus);
  }

  /**
   * List messages (paginated) visible to the current user
   */
  getMessages(page: number = 1, pageSize: number = 20, sortBy: string = 'CreatedDate', ascending: boolean = false, companyId?: string): Observable<PaginatedResponse<MessageDto>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sortBy', sortBy)
      .set('ascending', ascending.toString());
    if (companyId) {
      params = params.set('companyId', companyId);
    }
    return this.http.get<PaginatedResponse<MessageDto>>(this.apiUrl, { params });
  }

  /**
   * List messages sent by the current user (paginated)
   */
  getSentMessages(page: number = 1, pageSize: number = 20, sortBy: string = 'CreatedDate', ascending: boolean = false, companyId?: string): Observable<PaginatedResponse<MessageDto>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sortBy', sortBy)
      .set('ascending', ascending.toString());
    if (companyId) {
      params = params.set('companyId', companyId);
    }
    return this.http.get<PaginatedResponse<MessageDto>>(`${this.apiUrl}/sent`, { params });
  }

  /**
   * Get a single message by ID (optionally marks as read)
   */
  getMessage(id: string, markAsRead: boolean = true): Observable<MessageDto> {
    const params = new HttpParams().set('markAsRead', markAsRead.toString());
    return this.http.get<MessageDto>(`${this.apiUrl}/${id}`, { params });
  }

  /**
   * Get users the current user can send messages to
   */
  getPotentialRecipients(): Observable<MessageRecipientUserInfo[]> {
    return this.http.get<MessageRecipientUserInfo[]>(`${this.apiUrl}/potential-recipients`);
  }

  /**
   * Get the number of unread messages for the current user
   */
  getUnreadCount(): Observable<UnreadCountResponse> {
    return this.http.get<UnreadCountResponse>(`${this.apiUrl}/unread-count`);
  }

  /**
   * Get all answers/replies for a message
   */
  getAnswers(id: string): Observable<MessageAnswerDto[]> {
    return this.http.get<MessageAnswerDto[]>(`${this.apiUrl}/${id}/answers`);
  }

  /**
   * Download the file attachment of a message
   */
  getAttachment(id: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${id}/attachment`, { responseType: 'blob' });
  }

  /**
   * Create a new message
   */
  createMessage(request: MessageCreateRequest): Observable<MessageDto> {
    return this.http.post<MessageDto>(this.apiUrl, request);
  }

  /**
   * Reply to a message
   */
  replyToMessage(id: string, request: MessageReplyRequest): Observable<MessageAnswerDto> {
    return this.http.post<MessageAnswerDto>(`${this.apiUrl}/${id}/reply`, request);
  }

  /**
   * Upload or replace a file attachment on an existing message
   */
  uploadAttachment(id: string, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<void>(`${this.apiUrl}/${id}/attachment`, formData);
  }

  /**
   * Mark a message as read or unread for the current user
   */
  updateReadStatus(id: string, request: MessageReadStatusRequest): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${id}/read-status`, request);
  }

  /**
   * Mark all of the current user's unread messages as read
   */
  markAllAsRead(): Observable<{ markedCount: number }> {
    return this.http.post<{ markedCount: number }>(`${this.apiUrl}/mark-all-read`, {});
  }

  /**
   * Delete a message (sender or admin only)
   */
  deleteMessage(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }
}
